import styled, { css } from 'styled-components';

import { COLORS } from '../../../tokens/colors';
import { FONTS } from '../../../tokens/fonts';
import Throbber from '../../loaders/Throbber';
import { btnTypes, sizes } from './index';

const ELocalColors = {
    SecondaryHover: '#EFF3F7',
    WhiteHover: '#F2F2F2',
    GhostLoading: '#DEDEDE',
};

const getSize = (size) => {
    return sizes[size] || sizes['large'];
};

const getIconTextColors = (theColor) => {
    return css`
        color: ${theColor};
        fill: ${theColor};
        stroke: ${theColor};
    `;
};

const getAccentStyle = (p) => {
    if (p.type !== btnTypes.ACCENT) {
        return '';
    }

    return css`
        ${getIconTextColors(COLORS.TextWhite)};
        background-image: none;
        background-color: ${COLORS.ButtonAccent};


        ${!p.disabled && !p.isLoading && css`
            @media (hover: hover) {
                &:hover {
                    ${getIconTextColors(COLORS.TextWhite)};
                    background-image: none;
                    background-color: ${COLORS.Error};
                }
            }
        `}

        ${p.disabled && css`
            ${getIconTextColors(COLORS.TextHint)};
            background-image: none;
            background-color: ${COLORS.GRAY_LIGHT};
        `}
    `;
};

const getSecondaryStyle = (p) => {
    if (p.type !== btnTypes.SECONDARY) {
        return '';
    }

    return css`
        ${getIconTextColors(COLORS.TextPrimary)};
        background-image: none;
        background-color: ${COLORS.ButtonSecondary};

        ${!p.disabled && !p.isLoading && css`
            @media (hover: hover) {
                &:hover {
                    ${getIconTextColors(COLORS.TextPrimary)};
                    background-image: none;
                    background-color: ${ELocalColors.SecondaryHover};
                }
            }
        `}

        ${p.disabled && css`
            ${getIconTextColors(COLORS.TextHint)};
            background-image: none;
            background-color: ${COLORS.GRAY_LIGHT};
        `}
    `;
};

const getWhiteStyle = (p) => {
    if (p.type !== btnTypes.WHITE) {
        return '';
    }

    return css`
        ${getIconTextColors(COLORS.TextPrimary)};
        background-image: none;
        background-color: ${COLORS.ButtonWhite};

        ${!p.disabled && !p.isLoading && css`
            @media (hover: hover) {
                &:hover {
                    ${getIconTextColors(COLORS.TextPrimary)};
                    background-image: none;
                    background-color: ${ELocalColors.WhiteHover};
                }
            }
        `}

        ${p.disabled && css`
            ${getIconTextColors(COLORS.TextHint)};
            background-image: none;
            background-color: ${COLORS.GRAY_LIGHT};
        `}
    `;
};

const getGhostStyle = (p) => {
    if (p.type !== btnTypes.GHOST) {
        return '';
    }

    return css`
        ${getIconTextColors(COLORS.TextPrimary)};
        background-image: none;
        background-color: transparent;

        ${!p.disabled && !p.isLoading && css`
            @media (hover: hover) {
                &:hover {
                    ${getIconTextColors(COLORS.TextHint)};
                    background-color: transparent;
                }
            }
        `}

        ${p.disabled && css`
            ${getIconTextColors(COLORS.TextDisabled)};
            background-color: transparent;
            background-image: none;
        `}
    `;
};

export const Button = styled.div`
    /*** Primary style ***/

    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${p => p.isOnlyIcon
        ? `${getSize(p.size).iconPadding}px`
        : `${p.isIcon ? getSize(p.size).iconPadding : getSize(p.size).vPadding}px ${getSize(p.size).hPadding}px`
    };
    width: auto;
    max-width: 100%;
    opacity: ${p => p.isLoading ? '.6' : '1'};

    border: none;
    border-radius: 30px;
    box-shadow: none;

    ${p => p.size === 'small' ? FONTS.MediumXS : FONTS.MediumS};
    ${getIconTextColors(COLORS.TextWhite)};
    text-align: center;
    white-space: nowrap;

    background-color: ${COLORS.ButtonPrimary};
    transition: transform .2s;

    cursor: pointer;

    outline: none;

    ${p => !p.disabled && !p.isLoading && css`
        @media (hover: hover) {
            &:hover {
                ${getIconTextColors(COLORS.TextWhite)};
                background-color: ${COLORS.GRAY_DARK};
            }
        }

        &:active {
            transform: scale(${getSize(p.size).ratio});
        }
    `}

    ${p => p.disabled && css`
        ${getIconTextColors(COLORS.TextHint)};
        background-color: ${COLORS.GRAY_LIGHT};
        background-image: none;
        animation-name: none;
    `}

    /*** Accent style ***/
    ${p => getAccentStyle(p)}

    /*** Secondary style ***/
    ${p => getSecondaryStyle(p)}

    /*** White style ***/
    ${p => getWhiteStyle(p)}

    /*** Ghost style ***/
    ${p => getGhostStyle(p)}
`;

export const IconWrap = styled.div`
    flex: 0 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${p => getSize(p.size).iconSize}px;
    height: ${p => getSize(p.size).iconSize}px;
    margin-right: ${p => p.isOnlyIcon ? '0' : '8px'};
    overflow: hidden;
    pointer-events: none;
    cursor: inherit;

    svg {
        max-width: 100%;
        max-height: 100%;
        transform: ${p => p.isRotatedIcon ? 'rotate(180deg)' : 'rotate(0deg)'};
        transition: all 0.2s;
    }
`;

export const Inner = styled.div`
    display: block;
    white-space: nowrap;
    overflow: ${p => p.isLoading ? 'visible' : 'hidden'};
    text-overflow: ellipsis;
    pointer-events: none;
    cursor: inherit;

    span:nth-child(2) {
        margin-left: 20px;
    }
`;

export const SThrobber = styled(Throbber)`
    div{
        width: 15px;
        height: 15px;
        margin-top: 4px;
        margin-bottom: 4px;
    }
`;
